import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { ApiConstants } from 'src/app/base/api-constants';
import { getWorkflows } from 'src/app/STORE/actions/workflow.actions';
import { workflows } from 'src/app/STORE/selectors/workflow.selector';
import { setRuntimeWorkflowStatusField, updateAppDM } from 'src/app/STORE/actions/applicationdatamodel.actions';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import {
  RuntimeFieldConfigDialogComponent,
  TRtFieldConfigIF
} from '../runtime-field-config-dialog/runtime-field-config-dialog.component';
import { appDMFailData, appDMSuccData } from 'src/app/STORE/selectors/applicationdatamodel.selector';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { UtilService } from 'src/app/services/util.service';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { FsBaseService } from 'src/app/services/fs-base.service';
import { Subscription } from 'rxjs';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { allIndices } from 'src/app/STORE/selectors/indexdesigner.selector';
import { HttpBaseService } from 'src/app/base/http-base.service';

interface RuntimeConfigForm {
  orgId?: string;
  sid?: string;
  appId?: string;
  hadWorkflow?: boolean;
  hasWorkflow?: boolean;
  tableId?: string;
  valueOverride?: string[];
  visibilityOverride?: string[];
  sharedFields?: string[];
  statusField?: string | null;
  hasBusinessRules?: boolean;
  businessRules?: string[];
}

@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-runtime-config',
  templateUrl: './runtime-config.component.html',
  styleUrls: ['./runtime-config.component.scss'],
  providers: [DialogService],
  standalone: false //NOSONAR
})
export class RuntimeConfigComponent implements OnInit, OnChanges, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private store = inject(Store);
  private translateService = inject(TranslateService);
  private dialogService = inject(DialogService);
  private messageService = inject(MessageService);
  private loader = inject(AppLoaderService);
  private util = inject(UtilService);
  private appGlobalService = inject(AppGlobalService);
  private fsService = inject(FsBaseService);
  private httpBaseService = inject(HttpBaseService);

  @Input() formData: any;
  @Input() element: any;
  @Input() pickList: any;
  @Input() disableField = false;
  @Input() settingsData: any;

  isRuntimeWorkflowConfigured = false;
  isRuntimeBusinessRulesConfigured = false;
  isConfigureBtnEnabled = false;
  workflowSubs$ = null;
  isWorkflowsFetched = false;
  workflowList: any[] = [];
  primaryKeyFields: string[] = [];
  fieldsUsedInConfig: string[] = [];

  businessRuleEventsTooltip: string = '';
  // Status field vars
  wfStatusFields: any[];
  wfStatusFieldSuggestions: any[];
  selectedWorkflowStatus: any = null;
  backupRuntimeConfig: TRtFieldConfigIF;
  runtimeConfig: TRtFieldConfigIF;

  isBusinessRulesChanged = false;

  isLoading: boolean = false;
  appId: string = '';
  tableId: string = '';
  orgId: string = '';
  sid: string = '';
  initialCall: boolean = true;
  subscriptions: Subscription[] = [];

  statusField: string = '';
  flatRes: any = null;
  sharedFields: string[] = [];
  visibilityOverride: string[] = [];
  valueOverride: string[] = [];
  hasWorkflow: boolean = false;
  hadWorkflow: boolean = false;
  hasBusinessRules: boolean = false;
  businessRuleEventsConfigured: any[] = [];
  isRuntimeWorkflowRemoved: boolean = false;
  isBusinessRulesRemoved: boolean = false;
  indices: any = [];
  allFields: any[] = [];
  //business rule event vars
  businessRuleEvents: any[] = [
    { label: 'OnBeforeCreate', value: 'OnBeforeSave' },
    { label: 'OnAfterCreate', value: 'OnAfterSave' },
    { label: 'OnBeforeUpdate', value: 'OnBeforeUpdate' },
    { label: 'OnAfterUpdate', value: 'OnAfterUpdate' },
    { label: 'OnBeforeDelete', value: 'OnBeforeDelete' },
    { label: 'OnAfterDelete', value: 'OnAfterDelete' }
  ];
  selectedBusinessRuleEvents: any[] = [];
  fsDataSub: any;
  businessRuleEventsDropdownChange$: Subject<any> = new Subject<any>();
  private destroyBusinessRuleEventsDropdownChange$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  @Output() businessRulesConfigChanged = new EventEmitter<{ isEnabled: boolean }>();
  selectedCity: any = null;
  private allFields$ = new BehaviorSubject<any[]>([]);
  private rutimeDataSub$ = new BehaviorSubject<string>('');

  ngOnInit(): void {
    this.appId = this.appGlobalService.get('appId');
    this.orgId = this.appGlobalService.get('orgId');
    this.runtimeConfig = {
      shared_fields: [],
      statusField: '',
      value_override: [],
      visibility_override: [],
      businessRuleEvents: []
    };

    this.store
      .select(allIndices)
      .pipe(takeUntil(this.destroy$))
      .subscribe((indices: any) => {
        if (indices && indices.length > 0) {
          this.indices = indices;

          const primaryIndex = indices.find(
            (index: any) => index.indexType === 'PRIMARY' && Array.isArray(index.fields)
          );

          this.primaryKeyFields = primaryIndex?.fields || [];
        }
      });
    this.getDataFromFS();

    if (this.settingsData.deactivateRecords) {
      this.businessRuleEvents.push(
        ...[
          { label: 'OnBeforeActivate', value: 'OnBeforeActivate' },
          { label: 'OnAfterActivate', value: 'OnAfterActivate' },
          { label: 'OnBeforeDeactivate', value: 'OnBeforeDeactivate' },
          { label: 'OnAfterDeactivate', value: 'OnAfterDeactivate' }
        ]
      );
    }

    this.businessRulesConfigChanged.emit({
      isEnabled: this.isRuntimeBusinessRulesConfigured
    });

    this.businessRuleEventsDropdownChange$
      .pipe(debounceTime(2000), distinctUntilChanged(), takeUntil(this.destroyBusinessRuleEventsDropdownChange$))
      .subscribe(() => {
        this.onBusinessRuleEventsChange();
      });

    combineLatest([this.allFields$, this.rutimeDataSub$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.validateStatusField();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pickList?.currentValue?.allFieldsWithInfo?.length) {
      this.allFields = this.pickList?.allFieldsWithInfo || [];
      this.allFields$.next(this.allFields);
      this.wfStatusFields = this.pickList.allFieldsWithInfo.filter((field) => {
        if (field.f_type === 'Dropdown' && this.primaryKeyFields.indexOf(field.sid) === -1) {
          const parsedAllowedValuesObj = field.f_aValues ? JSON.parse(field.f_aValues) : null;
          if (parsedAllowedValuesObj?.values?.length) {
            return true;
          }
          return false;
        }
        return false;
      });

      this.wfStatusFieldSuggestions = [...this.wfStatusFields];

      if (this.runtimeConfig) {
        this.getFieldsUsedInConfig();
      }

      setTimeout(() => {
        this.selectedWorkflowStatus = this.wfStatusFieldSuggestions.find(
          (field) => field.sid === this.flatRes?.statusField
        );
        const exists = this.wfStatusFieldSuggestions.some((field) => field.sid === this.flatRes?.statusField);
      }, 100);
    }
  }

  /**
   * Listens the checkbox event and updates the t_runtime_config after validation
   * @param {any} $event
   * @returns a Promise
   */
  async onRuntimeWorkflowConfigChange($event): Promise<void> {
    if ($event.checked) {
      if (this.isDropdownFieldAvailable(true)) {
        //display the status field dropdown if there are dropdown fields available
        this.isRuntimeWorkflowConfigured = true;

        if (!this.isWorkflowsFetched) {
          await this.getWorkflows();
        }
        const tableWorkflows = this.workflowList.find((wf) => wf.tableId === this.formData?.sid);
        if (tableWorkflows?.workflowFieldId) {
          // Update runtimeworkflow after confirmation if workflow exists for the table in RD
          this.confirmationDialogService.confirm({
            message: this.translateService.instant('RT_ENABLE_CONF_MSG'),
            accept: () => {
              this.updateRuntimeConfiguration();
            },
            reject: () => {
              this.isRuntimeWorkflowConfigured = false;
            }
          });
        } else {
          // Update runtimeworkflow directly if no workflow available for this table
          this.updateRuntimeConfiguration();
        }
      } else {
        // Wait for DOM to check the checkbox visually and then uncheck it programmatically
        setTimeout(() => {
          this.isRuntimeWorkflowConfigured = false;
        }, 500);
      }
    } else {
      this.isRuntimeWorkflowConfigured = false;
      this.confirmationDialogService.confirm({
        message: this.translateService.instant('RT_DISABLE_CONF_MSG'),
        accept: () => {
          this.runtimeConfig.statusField = null;
          this.isRuntimeWorkflowRemoved = true;
          this.updateRuntimeConfiguration();
        },
        reject: () => {
          this.isRuntimeWorkflowConfigured = true;
        }
      });
    }
  }

  /**
   * Listens the checkbox event and updates the business rules
   * @param {any} $event
   * @returns a Promise
   */
  async onRuntimeBusinessRulesConfigChange($event): Promise<void> {
    this.isBusinessRulesChanged = true;
    // Emit the change to properties pane
    this.businessRulesConfigChanged.emit({
      isEnabled: this.isRuntimeBusinessRulesConfigured
    });
    this.isBusinessRulesChanged = false;

    if (!$event.checked) {
      this.hasBusinessRules = false;
      this.businessRuleEventsConfigured = [];
      this.isBusinessRulesRemoved = true;
      this.updateRuntimeConfiguration();
    } else {
      this.hasBusinessRules = true;
      this.updateRuntimeConfiguration();
    }
  }

  showConfirmationMessage() {
    if (this.isRuntimeWorkflowRemoved) {
      this.util.showMessage('success', this.translateService.instant('RT_WF_CONF_DELETED'));
      this.isRuntimeWorkflowRemoved = false;
    }
    if (this.isBusinessRulesRemoved) {
      this.util.showMessage('success', this.translateService.instant('RT_BR_CONF_DELETED'));
      this.isBusinessRulesRemoved = false;
    }
  }

  /**
   * Triggers the api call to update t_runtime_workflow
   * @param {boolean} state: Updated state of t_runtime_workflow
   */
  updateRuntimeConfiguration() {
    // Validate that status field is set when Runtime Workflow is enabled
    if (this.isRuntimeWorkflowConfigured && !this.selectedWorkflowStatus) {
      return; // Exit early without making the API call
    }

    this.loader.show();
    const updatedForm: RuntimeConfigForm = {
      orgId: this.formData.orgId,
      sid: this.formData.sid,
      appId: this.appId,
      hadWorkflow: this.hadWorkflow,
      hasWorkflow: this.hasWorkflow,
      tableId: this.formData.sid,
      valueOverride: this.valueOverride,
      visibilityOverride: this.visibilityOverride,
      sharedFields: this.sharedFields,
      statusField: this.statusField,
      hasBusinessRules: this.isRuntimeBusinessRulesConfigured,
      businessRules: this.businessRuleEventsConfigured
    };

    if (this.runtimeConfig) {
      if (!this.isRuntimeWorkflowConfigured) {
        this.runtimeConfig.statusField = undefined;
        this.selectedWorkflowStatus = null;
      }
      if (
        !this.isRuntimeBusinessRulesConfigured ||
        (this.isRuntimeBusinessRulesConfigured && !this.runtimeConfig?.businessRuleEvents)
      ) {
        this.runtimeConfig.businessRuleEvents = [];
        this.selectedBusinessRuleEvents = [];
      }

      updatedForm.sharedFields = this.runtimeConfig.shared_fields;
      updatedForm.statusField = this.runtimeConfig.statusField;
      updatedForm.visibilityOverride = this.runtimeConfig.visibility_override;
      updatedForm.valueOverride = this.runtimeConfig.value_override;
      updatedForm.hasWorkflow = !!this.runtimeConfig.statusField;
      if (updatedForm.hasBusinessRules === false) {
        updatedForm.businessRules = [];
      }
      if (updatedForm.hasWorkflow === false && updatedForm.hasBusinessRules === false) {
        updatedForm.visibilityOverride = [];
        updatedForm.valueOverride = [];
        updatedForm.sharedFields = [];
      }
    }
    const runtimeConfigSub = this.httpBaseService.put({ ...ApiConstants.updateRuntimeConfig }, updatedForm).subscribe(
      (res: any) => {
        this.loader.hide();
        this.isConfigureBtnEnabled = this.isRuntimeWorkflowConfigured || this.isRuntimeBusinessRulesConfigured;
        this.getFieldsUsedInConfig();
        this.showConfirmationMessage();
      },
      (error) => {
        this.loader.hide();
      }
    );
    this.subscriptions.push(runtimeConfigSub);
  }

  /**
   * It returns an promise indicating that workflows are fetched
   * @returns {Promise}
   */
  getWorkflows(): Promise<void> {
    const apiConstants = {
      tables: {
        ...ApiConstants.getAllTables
      },
      workflow: {
        ...ApiConstants.getWorkflows
      }
    };

    this.loader.show();
    this.store.dispatch(getWorkflows({ apiConstants }));

    return new Promise((resolve) => {
      const sub$ = this.store
        .select(workflows)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          if (res) {
            this.loader.hide();
            this.workflowList = res;
            this.isWorkflowsFetched = true;
            resolve();
          }
        });
      this.subscriptions.push(sub$);
    });
  }

  isDropdownFieldAvailable(showMessage = false): boolean {
    const dropdownFieldList = this.pickList?.allFieldsWithInfo?.filter((field) => {
      return field.f_type === 'Dropdown' && this.primaryKeyFields.indexOf(field.sid) === -1;
    });

    if (!dropdownFieldList.length) {
      if (showMessage) {
        this.showNoDropdownFieldConfigMessage();
      }
      return false;
    }
    // If there are dropdown fields check whether any field is having alllowed values
    let isAllowedValuesPresent = false;
    dropdownFieldList.forEach((ddField) => {
      const parsedObj = ddField.f_aValues ? JSON.parse(ddField.f_aValues) : null;
      if (parsedObj?.values?.length) {
        isAllowedValuesPresent = true;
        return;
      }
    });
    if (isAllowedValuesPresent) {
      return true;
    }
    if (showMessage) {
      this.showNoAllowedValuesConfigMessage();
    }
    return false;
  }

  showNoDropdownFieldConfigMessage() {
    this.messageService.add({
      life: 10000,
      severity: 'error',
      summary: this.translateService.instant('NO_DROPDOWN_SUMMARY'),
      detail: this.translateService.instant('NO_DROPDOWN_DETAIL')
    });
  }

  showNoAllowedValuesConfigMessage() {
    this.messageService.add({
      life: 10000,
      severity: 'error',
      summary: this.translateService.instant('NO_ALLOWED_VALUES_SUMMARY'),
      detail: this.translateService.instant('NO_ALLOWED_VALUES_DETAIL')
    });
  }

  /**
   * A method to trigger and display the RcFieldConfigDialogComponent as a dialog
   */
  showFieldConfigDialog(): void {
    const modalFormData = { ...this.formData };
    modalFormData['sharedFields'] = this.sharedFields;
    modalFormData['statusField'] = this.statusField;
    modalFormData['hasBusinessRules'] = this.hasBusinessRules;
    modalFormData['businessRules'] = this.businessRuleEventsConfigured;
    modalFormData['valueOverride'] = this.valueOverride;
    modalFormData['visibilityOverride'] = this.visibilityOverride;
    modalFormData['hasWorkflow'] = this.hasWorkflow;
    modalFormData['hadWorkflow'] = this.hadWorkflow;
    const ref = this.dialogService.open(RuntimeFieldConfigDialogComponent, {
      showHeader: false,
      styleClass: 'rc-config-dialog',
      autoZIndex: false,
      closeOnEscape: false,
      modal: true,
      data: {
        formData: modalFormData,
        pickList: this.pickList,
        disableField: this.disableField
      }
    });

    const refSubs = ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      // Update the runtime config once dialog is closed
      ref.destroy();
      refSubs.unsubscribe();
      this.getFieldsUsedInConfig();
    });
  }

  /**
   * Method to disable the field in the table, if the field is selected in the Workflow Status dropdown
   */
  handleWfStatusFieldChange() {
    if (!this.selectedWorkflowStatus) {
      // Show error message if no status field is selected but runtime workflow is enabled
      return;
    }
    this.runtimeConfig.statusField = this.selectedWorkflowStatus.sid;
    if (this.isRuntimeWorkflowConfigured) {
      if (!this.runtimeConfig.shared_fields?.includes(this.selectedWorkflowStatus.sid)) {
        if (!this.runtimeConfig.shared_fields) {
          this.runtimeConfig.shared_fields = [];
        }
        this.runtimeConfig.shared_fields.push(this.selectedWorkflowStatus.sid);
      }
      if (!this.runtimeConfig.visibility_override?.includes(this.selectedWorkflowStatus.sid)) {
        if (!this.runtimeConfig.visibility_override) {
          this.runtimeConfig.visibility_override = [];
        }
        this.runtimeConfig.visibility_override.push(this.selectedWorkflowStatus.sid);
      }
      if (!this.runtimeConfig.value_override?.includes(this.selectedWorkflowStatus.sid)) {
        if (!this.runtimeConfig.value_override) {
          this.runtimeConfig.value_override = [];
        }
        this.runtimeConfig.value_override.push(this.selectedWorkflowStatus.sid);
      }
    }

    this.updateRuntimeConfiguration();
  }

  onBusinessRuleEventsChange() {
    this.businessRuleEventsConfigured = this.selectedBusinessRuleEvents;
    this.businessRuleEventsTooltip = this.selectedBusinessRuleEvents.length
      ? this.selectedBusinessRuleEvents.join(', ')
      : '';
    this.updateRuntimeConfiguration();
    // Emit the change to properties pane
    this.businessRulesConfigChanged.emit({
      isEnabled: this.isRuntimeBusinessRulesConfigured
    });
  }

  getFieldsUsedInConfig() {
    this.fieldsUsedInConfig = [];
    const fieldsUsedInConfigSids = [
      ...new Set([
        ...new Set(this.runtimeConfig.shared_fields),
        ...new Set(this.runtimeConfig.visibility_override),
        ...new Set(this.runtimeConfig.value_override)
      ])
    ];
    this.pickList.allFieldsWithInfo.forEach((field) => {
      if (fieldsUsedInConfigSids.includes(field.sid)) {
        this.fieldsUsedInConfig.push(field.name);
      }
    });
  }

  getDataFromFS() {
    let version = this.appGlobalService.get('appVersion');
    const queryObject = {
      collectionName: `RuntimeConfiguration_${version}`,
      filter: [
        {
          field: 'appId',
          operator: '==',
          value: this.appId
        },
        {
          field: 'tableId',
          operator: '==',
          value: this.formData.sid
        }
      ]
    };
    this.isLoading = true;
    this.getRuntimeConfigurationData(queryObject);
  }

  getRuntimeConfigurationData(queryObject: any) {
    try {
      // Unsubscribe previous subscription if exists
      if (this.fsDataSub && typeof this.fsDataSub.unsubscribe === 'function') {
        this.fsDataSub.unsubscribe();
      }

      this.fsDataSub = this.fsService.fireService(queryObject).subscribe(
        (res: any) => {
          if (res && res.length > 0) {
            if (this.initialCall) {
              this.initialCall = false;
              const filteredRes =
                res?.filter((dt) => !dt.payload.doc.metadata.fromCache && !dt.payload.doc.data().recDeleted) || [];

              this.flatRes = filteredRes[0].payload.doc.data();
            } else {
              this.flatRes = res[0].payload.doc.data();
            }
            if (this.flatRes && this.flatRes.statusField != null) {
              this.isRuntimeWorkflowConfigured = true;
            } else this.isRuntimeWorkflowConfigured = false;

            this.tableId = this.flatRes.tableId;
            this.hasWorkflow = this.flatRes.hasWorkflow;
            this.hadWorkflow = this.flatRes.hadWorkflow;
            this.valueOverride = this.flatRes.valueOverride;
            this.visibilityOverride = this.flatRes.visibilityOverride;
            this.sharedFields = this.flatRes.sharedFields;
            this.statusField = this.flatRes.statusField;
            this.store.dispatch(
              setRuntimeWorkflowStatusField({ runtimeWorkflowStatusField: this.flatRes.statusField })
            );
            this.isRuntimeBusinessRulesConfigured = this.flatRes.hasBusinessRules;
            this.hasBusinessRules = this.flatRes.hasBusinessRules;
            this.isConfigureBtnEnabled = this.isRuntimeWorkflowConfigured || this.isRuntimeBusinessRulesConfigured;
            this.selectedBusinessRuleEvents = this.flatRes.businessRules;
            this.businessRuleEventsConfigured = this.flatRes.businessRules;
            this.businessRuleEventsTooltip = this.selectedBusinessRuleEvents?.length
              ? this.selectedBusinessRuleEvents.join(', ')
              : '';
            this.runtimeConfig.statusField = this.flatRes.statusField;
            this.runtimeConfig.shared_fields = this.flatRes.sharedFields;
            this.runtimeConfig.visibility_override = this.flatRes.visibilityOverride;
            this.runtimeConfig.value_override = this.flatRes.valueOverride;
            this.runtimeConfig.businessRuleEvents = this.flatRes.businessRules;
            this.selectedWorkflowStatus = this.wfStatusFields.find((field) => field.sid === this.flatRes.statusField);
            this.rutimeDataSub$.next(this.statusField);
            this.businessRulesConfigChanged.emit({
              isEnabled: this.isRuntimeBusinessRulesConfigured
            });
            this.getFieldsUsedInConfig();
          }
          this.isLoading = false;
        },
        (error) => {
          this.isLoading = false;
          console.error(error);
        }
      );
      this.subscriptions.push(this.fsDataSub);
    } catch (e) {
      this.isLoading = false;
      console.log(e);
    }
  }

  errorMsg: string = '';
  errorObj = {
    type: '',
    errorMsg: ''
  };
  validateStatusField() {
    if (this.isRuntimeWorkflowConfigured) {
      const field = this.allFields.find((f) => f.sid === this.statusField);
      if (!field) {
        this.util.handleDeletedIds([this.statusField], false, '', false, 'runtimeStatusFieldDeleted', (message) => {
          this.errorObj.type = 'error';
          this.errorObj.errorMsg = message;
        });
      } else if (field.f_type !== 'Dropdown') {
        this.util.handleDeletedIds(
          [this.statusField],
          false,
          '',
          false,
          'runtimeStatusFieldTypeMismatch',
          (message) => {
            this.errorObj.type = 'warning';
            this.errorObj.errorMsg = message;
          }
        );
      } else {
        this.errorObj = {
          type: '',
          errorMsg: ''
        };
      }
    }
  }
  showWarningPopup: boolean = false;
  toggleRuntimeConfigPopup() {
    this.showWarningPopup = !this.showWarningPopup;
  }

  ngOnDestroy(): void {
    // Ensure loader is hidden if component is destroyed during API calls
    this.loader.hide();

    // Complete all subjects to clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    this.destroyBusinessRuleEventsDropdownChange$.next();
    this.destroyBusinessRuleEventsDropdownChange$.complete();
    this.unsubscribeListener();
  }

  unsubscribeListener() {
    this.subscriptions?.forEach((k) => {
      if (k && typeof k.unsubscribe == 'function') {
        k.unsubscribe();
      }
    });
  }
}



